import { useEffect, useState } from 'react';
import { useDataStore } from '../store/dataStore';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  Package,
  Camera,
  Video,
  Plane,
  Trash2,
  Loader2,
  CheckCircle2
} from 'lucide-react';

export default function PackagesPage() {
  const {
    packages,
    packagesLoading,
    fetchPackages,
    addPackage,
    deletePackage
  } = useDataStore();

  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    includes_photo: true,
    includes_video: false,
    includes_drone: false,
    photo_count: '',
    video_duration: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    setIsSubmitting(true);
    try {
      await addPackage({
        ...formData,
        price: parseFloat(formData.price),
        photo_count: formData.photo_count ? parseInt(formData.photo_count) : null,
        video_duration: formData.video_duration ? parseInt(formData.video_duration) : null
      });
      toast.success('Paket eklendi');
      setIsOpen(false);
      setFormData({
        name: '',
        description: '',
        price: '',
        includes_photo: true,
        includes_video: false,
        includes_drone: false,
        photo_count: '',
        video_duration: ''
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`"${name}" paketini silmek istediğinize emin misiniz?`)) {
      try {
        await deletePackage(id);
        toast.success('Paket silindi');
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="packages-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Paketler</h1>
          <p className="text-muted-foreground mt-1">Hizmet paketlerinizi yönetin</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-package-btn">
              <Plus className="h-4 w-4 mr-2" />
              Yeni Paket
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Yeni Paket Ekle</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Paket Adı *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Örn: Premium Paket"
                  data-testid="package-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Paket detayları..."
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Fiyat (TL) *</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="0.00"
                  data-testid="package-price-input"
                />
              </div>
              <div className="space-y-3">
                <Label>İçerikler</Label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="photo"
                      checked={formData.includes_photo}
                      onCheckedChange={(checked) => setFormData({ ...formData, includes_photo: checked })}
                    />
                    <label htmlFor="photo" className="text-sm cursor-pointer">Fotoğraf Çekimi</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="video"
                      checked={formData.includes_video}
                      onCheckedChange={(checked) => setFormData({ ...formData, includes_video: checked })}
                    />
                    <label htmlFor="video" className="text-sm cursor-pointer">Video Çekimi</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="drone"
                      checked={formData.includes_drone}
                      onCheckedChange={(checked) => setFormData({ ...formData, includes_drone: checked })}
                    />
                    <label htmlFor="drone" className="text-sm cursor-pointer">Drone Çekimi</label>
                  </div>
                </div>
              </div>
              {formData.includes_photo && (
                <div className="space-y-2">
                  <Label>Fotoğraf Sayısı</Label>
                  <Input
                    type="number"
                    value={formData.photo_count}
                    onChange={(e) => setFormData({ ...formData, photo_count: e.target.value })}
                    placeholder="Örn: 300"
                  />
                </div>
              )}
              {formData.includes_video && (
                <div className="space-y-2">
                  <Label>Video Süresi (dakika)</Label>
                  <Input
                    type="number"
                    value={formData.video_duration}
                    onChange={(e) => setFormData({ ...formData, video_duration: e.target.value })}
                    placeholder="Örn: 30"
                  />
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)} className="flex-1">
                  İptal
                </Button>
                <Button type="submit" disabled={isSubmitting} className="flex-1" data-testid="package-submit-btn">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Packages Grid */}
      {packagesLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : packages.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {packages.map((pkg, index) => (
            <Card key={pkg.id} className={`animate-fade-in stagger-${(index % 5) + 1}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Package className="h-6 w-6 text-primary" />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(pkg.id, pkg.name)}
                    className="text-destructive hover:text-destructive h-8 w-8"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <h3 className="font-semibold text-lg mb-1">{pkg.name}</h3>
                <p className="text-2xl font-bold text-primary mb-3">
                  {formatCurrency(pkg.price)}
                </p>
                {pkg.description && (
                  <p className="text-sm text-muted-foreground mb-4">{pkg.description}</p>
                )}
                <div className="space-y-2">
                  {pkg.includes_photo && (
                    <div className="flex items-center gap-2 text-sm">
                      <Camera className="h-4 w-4 text-green-600" />
                      <span>Fotoğraf {pkg.photo_count && `(${pkg.photo_count} adet)`}</span>
                    </div>
                  )}
                  {pkg.includes_video && (
                    <div className="flex items-center gap-2 text-sm">
                      <Video className="h-4 w-4 text-green-600" />
                      <span>Video {pkg.video_duration && `(${pkg.video_duration} dk)`}</span>
                    </div>
                  )}
                  {pkg.includes_drone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Plane className="h-4 w-4 text-green-600" />
                      <span>Drone Çekimi</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
          <h3 className="text-lg font-medium mb-2">Henüz paket yok</h3>
          <p className="text-muted-foreground mb-4">Yeni paket ekleyerek başlayın</p>
        </div>
      )}
    </div>
  );
}
